import { AddShoppingCart, Checklist, ThreeSixty } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Chip, Link, Typography } from '@mui/material';
import ExerciseTabs from '@/components/exercise/exercise-tabs';
import Questions from '@/components/exercise/questions';
import LoginPrompt from '@/components/shared/login-prompt';
import { CourseTabList } from '@/constants/menu';
import { useAuth, useCourses } from '@/providers';
import type { Exercise as ExerciseModel } from '@/types';
import { useRoute } from '@/utils/client/route';

export default function Exercise({ exercise }: { exercise: ExerciseModel }) {
  const { getRouteUrl } = useRoute();
  const { isLoggedIn } = useAuth();
  const { selectedCourse, courseStatus, canRenewTest } = useCourses();
  const { articles, status: exerciseStatus, repeatable } = exercise;
  const isTest = selectedCourse?.type === 'Test';

  if (!selectedCourse)
    return (
      <Alert severity="error">
        Inhalte konten nicht geladen werden. Bitte kontaktiere den Support.
      </Alert>
    );

  const { status: courseSubmittedStatus } = courseStatus ?? {
    status: null,
  };

  return (
    <Box sx={{ pb: 10 }}>
      {isTest && courseSubmittedStatus === 'submitted' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Du hast den Kurs <Chip label="ausgewertet" size="small" variant="outlined" /> (siehe Menü
          oben rechts).
          {canRenewTest && (
            <>
              {' '}
              Du bist im Testrun{' '}
              <Chip
                label={`${courseStatus?.repetitions?.run}/${(courseStatus?.repetitions?.run ?? 0) + (courseStatus?.repetitions?.remaining ?? 0)}`}
                size="small"
                variant="outlined"
              />
              . Starte einen neuen Testrun durch{' '}
              <Box
                component="span"
                sx={{
                  verticalAlign: 'middle',
                  display: 'inline-flex',
                  mx: 0.5,
                }}>
                <ThreeSixty />
              </Box>
              NEUER TESTRUN in der Leiste unten.{' '}
              <Link
                href={getRouteUrl('myCourseTabs', {
                  courseSlug: selectedCourse.slug ?? '',
                  tabId: CourseTabList.fortschritt,
                })}>
                Hier geht es zu Deinen Testergebnissen.
              </Link>
            </>
          )}
          {courseStatus?.repetitions && courseStatus.repetitions.remaining === 0 && (
            <>
              Du bist im Testrun{' '}
              <Chip
                label={`${courseStatus.repetitions.run}/${courseStatus.repetitions.run + courseStatus.repetitions.remaining}`}
                size="small"
                sx={{ mr: 2 }}
                variant="outlined"
              />
              . Kaufe einen neuen Testrun durch{' '}
              <Box
                component="span"
                sx={{
                  verticalAlign: 'middle',
                  display: 'inline-flex',
                  mx: 0.5,
                }}>
                <AddShoppingCart />
              </Box>
              WARENKORB in der Leiste unten.
            </>
          )}
        </Alert>
      )}
      {exerciseStatus === 'active' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Aktiv</AlertTitle>
          Beantworte die Fragen und werte die Übung aus. Änderungen werden automatisch gespeichert.
        </Alert>
      )}
      {exerciseStatus === 'notStarted' && !isLoggedIn && (
        <Box sx={{ mb: 2 }}>
          <LoginPrompt>
            Registriere Dich, um diese Übung <strong>kostenlos</strong> nutzen zu können. Unten
            findest Du eine Vorschau der Übung.
          </LoginPrompt>
          <Typography variant="h2" mt={5} mb={3}>
            Vorschau der Übung
          </Typography>
        </Box>
      )}
      {exerciseStatus === 'submitted' && !isTest && repeatable === 'always' && (
        <Alert severity="info" icon={<Checklist />} sx={{ mb: 2 }}>
          <AlertTitle>Ausgewertet</AlertTitle>
          Du hast diese Übung ausgewertet. Unten findest Du Deine Ergebnisse. Du kannst die Übung
          stets wiederholen.
        </Alert>
      )}
      {exerciseStatus === 'submitted' && isTest && (
        <Typography variant="h2" mt={5} mb={3}>
          Auswertung
        </Typography>
      )}
      <Box>
        {articles && articles?.length > 0 ? (
          <ExerciseTabs exercise={exercise} />
        ) : (
          <Questions exercise={exercise} />
        )}
      </Box>
    </Box>
  );
}
